import { createClient } from '@/lib/supabase/client';

export interface WebinarPublicInfo {
  title: string;
  shortDescription: string;
  hostName: string;
  startTime: string;
  endTime: string;
  timezone: string;
  active: boolean;
  upcoming: string[];
}

export interface WebinarRegistrationInput {
  fullName: string;
  email: string;
  phone: string;
  sessionDate: string;
  everyWeek: boolean;
  businessStage: string;
  question: string;
  source: string;
  marketingConsent: boolean;
  website: string;
}

export interface WebinarSeries {
  title: string;
  shortDescription: string;
  startTime: string;
  endTime: string;
  startsOn: string;
  endsOn: string;
  zoomUrl: string;
  active: boolean;
}

export interface WebinarOverviewRow {
  sessionDate: string;
  count: number;
}

export interface WebinarRegistrationRow {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  sessionDate: string;
  everyWeek: boolean;
  businessStage: string;
  question: string;
  source: string;
  createdAt: string;
  attendedDates: string[];
  status: string;
}

export const JOURNEY_STAGES = [
  'Just exploring',
  'Planning to start',
  'Registered with CQC, not yet delivering',
  'Already delivering care',
];

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** Formats a YYYY-MM-DD string like "Wednesday 4 November 2026" without any timezone drift. */
export function formatSessionDate(value: string | null | undefined, withWeekday = true): string {
  if (!value) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) return value;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  const base = `${Number(m[3])} ${MONTHS[d.getUTCMonth()]} ${m[1]}`;
  return withWeekday ? `${WEEKDAYS[d.getUTCDay()]} ${base}` : base;
}

export function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/London' });
}

function pickDate(item: unknown): string | null {
  if (typeof item === 'string') return item.slice(0, 10);
  if (item && typeof item === 'object') {
    const o = item as Record<string, unknown>;
    const v = o.session_date ?? o.date ?? o.day;
    if (typeof v === 'string') return v.slice(0, 10);
  }
  return null;
}

function normaliseUpcoming(raw: unknown): string[] {
  let list: unknown = raw;
  if (typeof raw === 'string') {
    try {
      list = JSON.parse(raw);
    } catch {
      list = [];
    }
  }
  if (!Array.isArray(list)) return [];
  return list.map(pickDate).filter((d): d is string => !!d);
}

export async function fetchWebinarPublicInfo(): Promise<WebinarPublicInfo> {
  const supabase = createClient();
  const { data, error } = await supabase.rpc('webinar_public_info');
  if (error) throw new Error(error.message);
  const row: any = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error('Webinar details are not available right now.');
  return {
    title: row.title ?? '',
    shortDescription: row.short_description ?? '',
    hostName: row.host_name ?? '',
    startTime: row.start_time ?? '',
    endTime: row.end_time ?? '',
    timezone: row.timezone ?? 'Europe/London',
    active: row.active === true,
    upcoming: normaliseUpcoming(row.upcoming),
  };
}

async function readFunctionError(error: any): Promise<string> {
  let message: string = error?.message || 'Something went wrong. Please try again.';
  const ctx = error?.context;
  if (ctx && typeof ctx.json === 'function') {
    try {
      const parsed = await ctx.json();
      if (parsed?.error) message = String(parsed.error);
    } catch {
      // keep default message
    }
  }
  return message;
}

import { withUtmSource } from '@/lib/utm';

export async function registerForWebinar(input: WebinarRegistrationInput): Promise<{ sessionDate: string | null }> {
  const supabase = createClient();
  const { data, error } = await supabase.functions.invoke('webinar?action=register', {
    body: {
      full_name: input.fullName,
      email: input.email,
      phone: input.phone,
      session_date: input.sessionDate,
      every_week: input.everyWeek,
      business_stage: input.businessStage,
      question: input.question,
      source: withUtmSource(input.source),
      marketing_consent: input.marketingConsent,
      website: input.website,
    },
  });
  if (error) throw new Error(await readFunctionError(error));
  if (data?.error) throw new Error(String(data.error));
  if (!data?.ok) throw new Error('Registration could not be completed. Please try again.');
  return { sessionDate: typeof data.session_date === 'string' ? data.session_date : null };
}

/* ---------------------------- Superuser area ---------------------------- */

export async function fetchWebinarOverview(): Promise<WebinarOverviewRow[]> {
  const supabase = createClient();
  const { data, error } = await supabase.rpc('admin_webinar_overview');
  if (error) throw new Error(error.message);
  let list: any[] = Array.isArray(data) ? data : data ? [data] : [];
  return list
    .map((r) => {
      const date = pickDate(r);
      const count = Number(r.registrations ?? r.registered ?? r.count ?? r.total ?? r.registrations_count ?? 0);
      return date ? { sessionDate: date, count: Number.isFinite(count) ? count : 0 } : null;
    })
    .filter((r): r is WebinarOverviewRow => r !== null);
}

export async function fetchWebinarRegistrations(): Promise<WebinarRegistrationRow[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('webinar_registrations')
    .select(
      'id, full_name, email, phone, session_date, every_week, business_stage, question, source, created_at, attended_dates, status'
    )
    .order('session_date', { ascending: false })
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []).map((r: any) => ({
    id: r.id,
    fullName: r.full_name ?? '',
    email: r.email ?? '',
    phone: r.phone ?? '',
    sessionDate: r.session_date ?? '',
    everyWeek: r.every_week === true,
    businessStage: r.business_stage ?? '',
    question: r.question ?? '',
    source: r.source ?? '',
    createdAt: r.created_at ?? '',
    attendedDates: Array.isArray(r.attended_dates) ? r.attended_dates.map((d: string) => String(d).slice(0, 10)) : [],
    status: r.status ?? 'registered',
  }));
}

export async function setAttended(registrationId: string, dates: string[]): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase.from('webinar_registrations').update({ attended_dates: dates }).eq('id', registrationId);
  if (error) throw new Error(error.message);
}

const trimTime = (t: string | null | undefined) => (t ? String(t).slice(0, 5) : '');

export async function fetchWebinarSeries(): Promise<WebinarSeries | null> {
  const supabase = createClient();
  const { data, error } = await supabase.from('webinar_series').select('*').eq('id', 'weekly').maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) return null;
  return {
    title: data.title ?? '',
    shortDescription: data.short_description ?? '',
    startTime: trimTime(data.start_time),
    endTime: trimTime(data.end_time),
    startsOn: data.starts_on ?? '',
    endsOn: data.ends_on ?? '',
    zoomUrl: data.zoom_url ?? '',
    active: data.active === true,
  };
}

export async function saveWebinarSeries(s: WebinarSeries): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('webinar_series')
    .update({
      title: s.title.trim(),
      short_description: s.shortDescription.trim(),
      start_time: s.startTime.length === 5 ? `${s.startTime}:00` : s.startTime,
      end_time: s.endTime.length === 5 ? `${s.endTime}:00` : s.endTime,
      starts_on: s.startsOn,
      ends_on: s.endsOn ? s.endsOn : null,
      zoom_url: s.zoomUrl.trim(),
      active: s.active,
      updated_at: new Date().toISOString(),
    })
    .eq('id', 'weekly');
  if (error) throw new Error(error.message);
}

function csvCell(v: string): string {
  const needsGuard = /^[=+\-@\t\r]/.test(v);
  const safe = needsGuard ? `'${v}` : v;
  return `"${safe.replace(/"/g, '""')}"`;
}

export function registrationsToCsv(rows: WebinarRegistrationRow[]): string {
  const head = [
    'Name',
    'Email',
    'Phone',
    'Session date',
    'Weekly',
    'Journey stage',
    'Question',
    'Source',
    'Registered',
    'Attended dates',
  ];
  const lines = rows.map((r) =>
    [
      r.fullName,
      r.email,
      r.phone,
      r.sessionDate,
      r.everyWeek ? 'Yes' : 'No',
      r.businessStage,
      r.question,
      r.source,
      r.createdAt ? r.createdAt.slice(0, 10) : '',
      r.attendedDates.join(' '),
    ]
      .map((v) => csvCell(String(v ?? '')))
      .join(',')
  );
  return [head.map(csvCell).join(','), ...lines].join('\r\n');
}
